import React from "react";
import { connect } from "react-redux";
import { InlineNotification, Modal } from "carbon-components-react";

import { AppShape, HLHLContactRole } from "@eave/shared/types/api";
import { TRUS_POSTPONE_UW_KEY } from "@eave/shared/config/localStorageKeys";

import { ReportingEventKinds } from "web_modules/Events/types";
import { LoanStatus } from "web_modules/shared/loan_statuses";
import {
  mapIfExists,
  removeTimestampForID,
  savePromptDismissedEvent,
  serializeMap,
  shouldPromptToBeginUnderwriting,
  updateLoanStatus,
} from "./helpers";

interface Props {
  app: AppShape;
  loadApp: Function;
  hlhlContactRole?: HLHLContactRole;
}

const StartUnderwritingPrompt: React.FC<Props> = ({ app, loadApp, hlhlContactRole }: Props) => {
  const { id, borrower1_last_name, has_borrower2, borrower2_last_name, underwriting_summary } = app;

  const borrowers = has_borrower2
    ? `${borrower1_last_name} and ${borrower2_last_name}`
    : `${borrower1_last_name}`;

  const shouldPrompt = shouldPromptToBeginUnderwriting(app, hlhlContactRole);

  const handlePromptDismissal = async () => {
    // Save a note to Notes & Events
    await savePromptDismissedEvent(id, ReportingEventKinds.UnderwritingDismissed);

    // Set a key in localStorage with a timestamp
    const dismissedAppsMap = mapIfExists() || new Map();
    dismissedAppsMap.set(id, `${Date.now()}`);
    localStorage.setItem(TRUS_POSTPONE_UW_KEY, serializeMap(dismissedAppsMap));

    loadApp(id);
  };

  const handlePromptConfirm = async () => {
    await updateLoanStatus(id, LoanStatus.InBorrowerUnderwriting, underwriting_summary);

    const dismissedAppsMap = mapIfExists();
    removeTimestampForID(dismissedAppsMap, id);
    loadApp(id);
  };

  return (
    <Modal
      open={shouldPrompt}
      preventCloseOnClickOutside
      modalHeading="Ready to begin underwriting?"
      modalLabel="TRUS: Underwriting"
      primaryButtonText="Start underwriting"
      secondaryButtonText="Not yet"
      onRequestClose={handlePromptDismissal}
      onRequestSubmit={handlePromptConfirm}
    >
      <p style={{ marginBottom: "1rem" }}>
        This is the loan application for <strong>{borrowers}</strong>.
      </p>
      <p>
        Change the <strong>Loan Status</strong> to <em>In Borrower Underwriting?</em>
      </p>
      <InlineNotification
        title="WARNING"
        subtitle="Do not click Start Underwriting unless you are an Underwriter and ready to begin underwriting the file. Otherwise click Not Yet."
        role="alert"
        kind="warning"
        notificationType="inline"
        statusIconDescription="Warning"
        hideCloseButton
        lowContrast
      />
    </Modal>
  );
};

const mapStateToProps = state => ({
  hlhlContactRole: state.hlhlContactsRole.hlhl_contact_role,
});

export default connect(mapStateToProps, null)(StartUnderwritingPrompt);
